import "./SubscriptionInvoice.css";
import { useLocation, useNavigate } from "react-router-dom";
import toWords from "number-to-words";
import useFormattingUtils from "../../../../context/useFormattingUtils";

const PAISAVARA_LOGO = "https://paisavara.com/PVLOGO.png";

const SubscriptionInvoice = () => {
  const location = useLocation();
  const invoiceData = location.state?.invoiceData;
  const navigate = useNavigate();
  const { formatDate, formatCurrency } = useFormattingUtils();

  const transformInvoiceData = (apiData) => {
    if (!apiData || !apiData.results || apiData.results.length === 0) return null;

    const invoice = apiData.results[0];
    const storeInfo = apiData.store_info || {};
    const parsedTotalAmount = Number.parseFloat(invoice.total_amount);
    const totalAmount = Number.isFinite(parsedTotalAmount) ? parsedTotalAmount : 0;
    const currencySymbol = formatCurrency(0).replace(/[\d.,\s]/g, "");
    const integerPart = Math.floor(totalAmount);
    const decimalPart = Math.round((totalAmount % 1) * 100);
    const invoiceShortId = String(invoice.id || "").substring(0, 8).toUpperCase();

    let amountInWords = "";
    if (currencySymbol === "₹") {
      amountInWords = `Rupees ${toWords.toWords(integerPart)}`;
      if (decimalPart > 0) amountInWords += ` and ${toWords.toWords(decimalPart)} Paise`;
    } else if (currencySymbol === "$") {
      amountInWords = `${toWords.toWords(integerPart)} Dollars`;
      if (decimalPart > 0) amountInWords += ` and ${toWords.toWords(decimalPart)} Cents`;
    } else if (currencySymbol === "£") {
      amountInWords = `${toWords.toWords(integerPart)} Pounds`;
      if (decimalPart > 0) amountInWords += ` and ${toWords.toWords(decimalPart)} Pence`;
    } else {
      amountInWords = toWords.toWords(integerPart);
      if (decimalPart > 0) amountInWords += ` and ${toWords.toWords(decimalPart)}`;
    }

    return {
      invoiceId: `PV-SUB-${invoiceShortId}`,
      orderId: invoiceShortId,
      date: invoice.date,
      billedTo: {
        name: storeInfo.store_name || "Store",
        address: [
          storeInfo.store_address_line1,
          storeInfo.store_city,
          storeInfo.store_state,
          storeInfo.store_country,
          storeInfo.store_pincode,
        ].filter(Boolean).join(", "),
      },
      subscriptions: (invoice.feature_entriey_invocies || []).map((feature) => ({
        title: feature.feature_title,
        duration: `${feature.no_of_months} month${feature.no_of_months !== 1 ? "s" : ""}`,
        amount: Number(feature.amount || 0),
        activeTill: feature.end_date,
        ref: `S${invoiceShortId}`,
      })),
      subtotal: totalAmount * 0.98,
      tax: totalAmount * 0.02,
      total: totalAmount,
      amountInWords: `${amountInWords} only`,
    };
  };

  const transformedData = transformInvoiceData(invoiceData);

  if (!transformedData) {
    return (
      <div className="subscription-invoice-page">
        <div className="subscription-invoice-empty">No invoice data available</div>
      </div>
    );
  }

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="subscription-invoice-page">
      <div className="subscription-invoice-toolbar">
        <button onClick={handleBack} className="subscription-invoice-back-btn" aria-label="Go back">
          Back
        </button>
        <button onClick={() => window.print()} className="subscription-invoice-print-btn">
          Print Invoice
        </button>
      </div>

      <article className="subscription-invoice-document">
        <header className="subscription-invoice-top">
          <div className="subscription-invoice-brand">
            <img src={PAISAVARA_LOGO} alt="PaisaVara" />
            <div>
              <span>Invoice From</span>
              <h1>PaisaVara.com</h1>
              <p>Subscription and workspace services</p>
            </div>
          </div>
          <div className="subscription-invoice-title-block">
            <span>Tax Invoice</span>
            <strong>{transformedData.invoiceId}</strong>
            <p>Date: {formatDate(transformedData.date)}</p>
          </div>
        </header>

        <section className="subscription-invoice-party-grid">
          <div className="subscription-invoice-party-card">
            <span>Supplier</span>
            <h2>PaisaVara.com</h2>
            <p>Business software subscriptions</p>
            <p>India</p>
          </div>
          <div className="subscription-invoice-party-card">
            <span>Billed To</span>
            <h2>{transformedData.billedTo.name}</h2>
            <p>{transformedData.billedTo.address || "Store address not available"}</p>
          </div>
          <div className="subscription-invoice-summary-card">
            <span>Total Paid</span>
            <strong>{formatCurrency(transformedData.total)}</strong>
            <p>Payment received for selected modules</p>
          </div>
        </section>

        <section className="subscription-invoice-lines">
          <div className="subscription-invoice-lines-header">
            <h2>Subscription Details</h2>
            <span>{transformedData.subscriptions.length} item(s)</span>
          </div>

          <div className="subscription-invoice-table-wrap">
            <table className="subscription-invoice-table">
              <thead>
                <tr>
                  <th>No</th>
                  <th>Module</th>
                  <th>Reference</th>
                  <th>Duration</th>
                  <th>Active Till</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {transformedData.subscriptions.map((item, i) => (
                  <tr key={`${item.title}-${i}`}>
                    <td>{i + 1}</td>
                    <td>
                      <strong>{item.title}</strong>
                      <span>Workspace module subscription</span>
                    </td>
                    <td>{item.ref}</td>
                    <td>{item.duration}</td>
                    <td>{formatDate(item.activeTill)}</td>
                    <td>{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="subscription-invoice-total-grid">
          <div className="subscription-invoice-words">
            <span>Amount in Words</span>
            <p>{transformedData.amountInWords}</p>
          </div>

          <div className="subscription-invoice-totals-card">
            <div>
              <span>Subtotal</span>
              <strong>{formatCurrency(transformedData.subtotal)}</strong>
            </div>
            <div>
              <span>Tax / Fees</span>
              <strong>{formatCurrency(transformedData.tax)}</strong>
            </div>
            <div className="subscription-invoice-grand-total">
              <span>Total</span>
              <strong>{formatCurrency(transformedData.total)}</strong>
            </div>
          </div>
        </section>

        <footer className="subscription-invoice-footer">
          <p>Thank you for choosing PaisaVara. This invoice was generated electronically for your records.</p>
          <span>Product is by</span>
          <img src={PAISAVARA_LOGO} alt="PaisaVara" />
        </footer>
      </article>
    </div>
  );
};

export default SubscriptionInvoice;
